import React from 'react';
import clsx from 'clsx';
import { AlertTriangle, CheckCircle2, MapPin } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { CodeText } from '../../components/CodeText';
import { formatQuantity } from '../../../../utils/inventoryLabels';
import {
  focusItemOf,
  itemPending,
  splitByOrder,
  splitText,
  stopProgressText,
  takeQuantityOf,
} from '../../../../features/operatorPicking/operatorPickingRouteUtils';

/**
 * An item of the stop: what it lacks, what was counted of it and for which
 * orders. The card itself is not a button: tapping it counts nothing. «+1»
 * and «Falta» show once the stop is verified.
 */
const RouteItem = ({ item, counted, focused, verified, ordersCount, locked, onAddOne, onShort }) => {
  const pending = itemPending(item);
  const done = pending === 0;
  const split = done ? '' : splitText(splitByOrder(item, takeQuantityOf(item, counted)), ordersCount);

  return (
    <li
      data-testid="route-item"
      data-key={item.key}
      data-pending={pending}
      data-counted={counted}
      className={clsx(
        'space-y-3 rounded-2xl border-2 bg-white p-4 dark:bg-[#1a1a1a]',
        verified && focused && !done ? 'border-ninja-green' : 'border-border'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-lg font-bold text-ninja-dark dark:text-white">{item.sku}</p>
          <p className="text-sm text-text-secondary">{item.name}</p>
          {item.lot && <p className="text-sm font-semibold text-ninja-dark dark:text-white">Lote {item.lot}</p>}
        </div>
        {done ? (
          <p className="flex shrink-0 items-center gap-1.5 text-sm font-semibold text-ninja-dark dark:text-white">
            <CheckCircle2 size={20} aria-hidden="true" className="text-ninja-green" />
            Tomado
          </p>
        ) : (
          <div className="shrink-0 text-right">
            <p className="text-2xl font-bold text-ninja-dark dark:text-white">
              {formatQuantity(counted)} / {formatQuantity(pending)}
            </p>
            <p className="text-xs text-text-secondary">escaneadas</p>
          </div>
        )}
      </div>
      {split && (
        <p data-testid="route-item-split" className="text-sm font-semibold text-ninja-dark dark:text-white">
          {split}
        </p>
      )}
      {verified && !done && (
        <div className="flex gap-3">
          <Button
            variant="outline"
            size="lg"
            disabled={locked || counted >= pending}
            aria-label={`+1 de ${item.sku}`}
            onClick={() => onAddOne(item.key)}
            data-testid="route-item-add-one"
          >
            +1
          </Button>
          <Button variant="ghost" size="lg" disabled={locked} onClick={() => onShort(item.key)} data-testid="route-item-short">
            Falta
          </Button>
        </div>
      )}
    </li>
  );
};

/**
 * The stop on screen: where to go — «Ve a A-01», «Parada 2 de 7» — and, once
 * its slot is scanned, what to take there. Until then «Ya estoy en A-01» reads
 * the slot's code, as scanning its label would. The scan panel goes between
 * the two (`children`), under the eye and the thumb.
 *
 * @param {object} props
 * @param {object} props.route
 * @param {object} props.stop - The route's stop.
 * @param {{index: number, verified: boolean, itemKey: string|null}} props.position - Where the walk is.
 * @param {Record<string, number>} props.localCounts
 * @param {boolean} props.locked - A write is out or unconfirmed: nothing counts.
 * @param {(code: string) => void} props.onVerify - Reads the slot's code.
 * @param {(key: string) => void} props.onAddOne
 * @param {(key: string) => void} props.onShort
 * @param {React.ReactNode} [props.children]
 */
export const RouteStopCard = ({ route, stop, position, localCounts, locked, onVerify, onAddOne, onShort, children }) => {
  const { verified } = position;
  const focusKey = focusItemOf(stop, position.itemKey)?.key;

  return (
    <>
      <section
        data-testid="route-stop"
        data-slot-code={stop.slot_code}
        data-verified={String(verified)}
        className={clsx(
          'space-y-2 rounded-3xl border-2 bg-white p-4 dark:bg-[#1a1a1a]',
          verified ? 'border-ninja-green' : 'border-border'
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-2xl font-bold text-ninja-dark dark:text-white">
              <MapPin size={24} aria-hidden="true" className="shrink-0 text-ninja-green" />
              <CodeText text={verified ? `En ${stop.slot_code}` : `Ve a ${stop.slot_code}`} />
            </p>
            {stop.slot_name && stop.slot_name !== stop.slot_code && (
              <p className="text-sm text-text-secondary">{stop.slot_name}</p>
            )}
          </div>
          <p data-testid="route-stop-progress" className="shrink-0 text-sm font-semibold text-ninja-dark dark:text-white">
            {stopProgressText(position.index, route)}
          </p>
        </div>
        {/* Arriving is said as what the operator did: it reads the slot's code, as scanning it would. */}
        {!verified && (
          <Button
            variant="primary"
            size="lg"
            fullWidth
            icon={MapPin}
            disabled={locked}
            onClick={() => onVerify(stop.slot_code)}
            data-testid="route-stop-verify"
            data-code={stop.slot_code}
          >
            <span>
              <CodeText text={`Ya estoy en ${stop.slot_code}`} />
            </span>
          </Button>
        )}
        {stop.under_count && (
          <p className="flex items-start gap-2 text-sm font-semibold text-ninja-dark dark:text-white">
            <AlertTriangle size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
            Hay un faltante abierto aquí: puede haber menos de lo que dice el sistema.
          </p>
        )}
      </section>

      {children}

      <section aria-labelledby="route-items-title" className="space-y-3">
        <h2 id="route-items-title" className="text-sm font-semibold text-text-secondary">
          {verified ? 'Toma aquí' : 'Aquí vas a tomar'}
        </h2>
        <ul className="space-y-3">
          {(stop.items || []).map((item) => (
            <RouteItem
              key={item.key}
              item={item}
              counted={localCounts[item.key] || 0}
              focused={item.key === focusKey}
              verified={verified}
              ordersCount={(route.orders || []).length}
              locked={locked}
              onAddOne={onAddOne}
              onShort={onShort}
            />
          ))}
        </ul>
      </section>
    </>
  );
};
